import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoginButton } from '../components/Auth';
import { Play, BookOpen, Brain, Award, Users, CheckCircle, ChevronDown, Mail, X, Database, Languages, Sparkles } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PromoView - Landing page component
 * Shows app details, features, video and login options
 */
function PromoView({ onStart }) {
  const [showVideo, setShowVideo] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [stats, setStats] = useState({ totalCourses: 81, totalQuestions: 8000, totalVideos: 200 });
  const [totalStats, setTotalStats] = useState({
    total_questions: 180000,
    gov_exam_questions: 113000,
    language_count: 3,
  });
  const [animatedCount, setAnimatedCount] = useState(0);
  const [specializations, setSpecializations] = useState([
    { name: 'רפואה', courses: 43, questions: '20,000+', icon: '🩺' },
    { name: 'סיעוד', courses: 28, questions: '6,000+', icon: '🏥' },
    { name: 'חינוך', courses: 32, questions: '1,200+', icon: '📚' },
    { name: 'משפטים', courses: 21, questions: '1,200+', icon: '⚖️' },
    { name: 'רפואת שיניים', courses: 0, questions: '0', icon: '🦷' },
    { name: 'רוקחות', courses: 0, questions: '0', icon: '💊' },
    { name: 'מבחני רישוי', courses: 0, questions: '0', icon: '📋' },
  ]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [specsRes, statsRes] = await Promise.all([
          axios.get(`${API}/specializations`),
          axios.get(`${API}/stats`)
        ]);
        
        const specs = specsRes.data;
        const statsData = statsRes.data;
        
        // Update specializations with real data
        const specMap = {
          'medicine': { icon: '🩺', name: 'רפואה' },
          'nursing': { icon: '🏥', name: 'סיעוד' },
          'education': { icon: '📚', name: 'חינוך' },
          'law': { icon: '⚖️', name: 'משפטים' },
          'dentistry': { icon: '🦷', name: 'רפואת שיניים' },
          'pharmacy': { icon: '💊', name: 'רוקחות' },
          'gov-exams': { icon: '📋', name: 'מבחני רישוי' },
        };
        
        const updatedSpecs = specs
          .filter(s => specMap[s.id])
          .map(s => ({
            name: specMap[s.id].name,
            icon: specMap[s.id].icon,
            courses: s.courses?.length || 0,
            questions: statsData?.by_specialization?.[s.id]?.questions?.toLocaleString() + '+' || '1,000+'
          }))
          .filter(s => s.courses > 0);
        
        if (updatedSpecs.length > 0) {
          setSpecializations(updatedSpecs);
        }
        
        if (statsData) {
          setStats({
            totalCourses: statsData.total_courses || 81,
            totalQuestions: statsData.total_questions || 8000,
            totalVideos: statsData.total_videos || 200
          });
        }
      } catch (e) {
        console.log('Using default stats');
      }
    };
    loadStats();

    // Fetch total question stats for the marketing banner
    const loadTotalStats = async () => {
      try {
        const r = await axios.get(`${API}/questions/total-stats`);
        setTotalStats({
          total_questions: r.data.total_questions || 180000,
          gov_exam_questions: r.data.gov_exam_questions || 113000,
          language_count: r.data.language_count || 3,
        });
      } catch (e) {
        console.log('Using default total stats');
      }
    };
    loadTotalStats();
  }, []);

  // Animated counter for the hero banner (rolls up on mount / stats change)
  useEffect(() => {
    const target = totalStats.total_questions;
    const duration = 1500;
    const startTime = performance.now();
    let rafId;
    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedCount(Math.floor(target * eased));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [totalStats.total_questions]);

  const features = [
    { icon: BookOpen, title: 'קורסים מקיפים', desc: `${stats.totalCourses}+ קורסים ברפואה, סיעוד, משפטים וחינוך`, color: 'text-teal-400' },
    { icon: Brain, title: 'למידה אדפטיבית', desc: 'שאלות מותאמות לרמה שלך', color: 'text-blue-400' },
    { icon: Award, title: 'מבחני רישוי', desc: 'סימולטורים מלאים למבחנים ממשלתיים', color: 'text-amber-400' },
    { icon: Users, title: 'תמיכה מלאה', desc: 'ליווי וסרטוני הסבר לכל נושא', color: 'text-rose-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white" dir="rtl">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-teal-500/20 rounded-full blur-[150px] -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[150px] translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-12 sm:py-20">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left - Text Content */}
            <div className="flex-1 text-center lg:text-right">
              <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-bold mb-6">
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></span>
                פלטפורמת הלמידה המובילה בישראל
              </div>
              
              <h1 className="text-5xl sm:text-7xl font-black mb-6 tracking-tight">
                <img 
                  src="/logo.png" 
                  alt="הצ'ייסר" 
                  className="h-20 sm:h-28 mx-auto lg:mx-0 lg:mr-0 object-contain select-none pointer-events-none"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </h1>
              
              <p className="text-xl sm:text-2xl font-bold text-slate-300 mb-4 leading-relaxed">
                הדרך הקלה והחכמה <span className="text-teal-400">ללמוד ולהצליח</span>
              </p>
              <p className="text-lg sm:text-xl font-medium text-slate-500 mb-8" lang="ar" style={{ fontFamily: "'Noto Sans Arabic', 'Heebo', sans-serif", letterSpacing: '0', wordSpacing: '-0.02em' }}>
                الطريقة الأسهل والأذكى <span className="text-teal-400">للتعلم والنجاح</span>
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={onStart}
                  className="bg-teal-500 text-slate-900 px-10 py-4 rounded-xl text-lg font-black shadow-[0_15px_40px_rgba(20,184,166,0.3)] active:scale-95 transition-all hover:bg-teal-400"
                  data-testid="start-button"
                >
                  כניסה מהירה
                </button>
                <LoginButton className="bg-white/10 backdrop-blur text-white px-10 py-4 rounded-xl text-lg font-bold flex items-center gap-3 justify-center hover:bg-white/20 transition-colors border border-white/20">
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                  התחבר עם Google
                </LoginButton>
              </div>

              {/* Content Bank Banner - social proof */}
              <div
                className="mt-10 relative overflow-hidden rounded-2xl border border-teal-500/30 bg-gradient-to-l from-slate-800/80 via-teal-950/40 to-slate-800/80 backdrop-blur px-6 py-5 shadow-[0_10px_40px_rgba(20,184,166,0.15)]"
                data-testid="content-bank-banner"
              >
                {/* Ambient glow */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-teal-400/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-emerald-400/20 rounded-full blur-3xl pointer-events-none" />

                <div className="relative flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="bg-teal-500/20 rounded-xl p-2.5 border border-teal-400/30">
                      <Database className="w-6 h-6 text-teal-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className="text-3xl sm:text-4xl font-black text-white tabular-nums"
                          data-testid="total-questions-count"
                        >
                          {animatedCount.toLocaleString('he-IL')}
                        </span>
                        <span className="text-teal-300 font-black text-lg">+</span>
                      </div>
                      <div className="text-sm text-slate-300 font-bold">שאלות מקצועיות במאגר</div>
                    </div>
                  </div>

                  <div className="hidden sm:block h-12 w-px bg-white/10" />

                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-500/20 rounded-xl p-2.5 border border-emerald-400/30">
                      <Languages className="w-6 h-6 text-emerald-300" />
                    </div>
                    <div>
                      <div className="text-3xl sm:text-4xl font-black text-white">3</div>
                      <div className="text-sm text-slate-300 font-bold">שפות: 🇮🇱 🇺🇸 🇸🇦</div>
                    </div>
                  </div>

                  <div className="hidden sm:block h-12 w-px bg-white/10" />

                  <div className="flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 rounded-full px-3 py-1.5">
                    <Sparkles className="w-4 h-4 text-teal-300 animate-pulse" />
                    <span className="text-xs font-black text-teal-200">מבחני רישוי אמיתיים</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Video/Image */}
            <div className="flex-1 w-full max-w-lg">
              <div 
                className="relative aspect-video bg-slate-800 rounded-3xl overflow-hidden shadow-2xl border border-white/10 cursor-pointer group"
                onClick={() => setShowVideo(true)}
              >
                {!showVideo ? (
                  <>
                    {/* Video Thumbnail */}
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20"></div>
                    <video 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="w-full h-full object-cover opacity-60"
                    >
                      <source src="/intro.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-white mr-[-4px]" fill="white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-center">
                      <p className="text-white font-bold text-sm bg-black/50 backdrop-blur px-4 py-2 rounded-full inline-block">
                        צפה בסרטון ההיכרות
                      </p>
                    </div>
                  </>
                ) : (
                  <video 
                    controls 
                    autoPlay
                    className="w-full h-full"
                  >
                    <source src="/intro.mp4" type="video/mp4" />
                  </video>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-800/50 border-y border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { value: stats.totalCourses.toString(), label: 'קורסים', suffix: '+' },
              { value: Math.floor(stats.totalQuestions / 1000).toString(), label: 'שאלות', suffix: 'K+' },
              { value: stats.totalVideos.toString(), label: 'סרטונים', suffix: '+' },
              { value: '5', label: 'מבחני רישוי', suffix: '' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-teal-400">{stat.value}<span className="text-2xl">{stat.suffix}</span></div>
                <div className="text-sm font-bold text-slate-500 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">למה הצ'ייסר?</h2>
            <p className="text-slate-400 text-lg">כל מה שצריך להצליח במבחנים - במקום אחד</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="bg-slate-800/50 p-6 rounded-2xl border border-white/10 hover:border-teal-500/50 transition-all group">
                <div className={`w-14 h-14 rounded-xl bg-slate-700/50 flex items-center justify-center mb-4 ${feature.color} group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specializations */}
      <div className="py-16 bg-slate-800/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">התמחויות</h2>
            <p className="text-slate-400 text-lg">בחר את המסלול שלך</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {specializations.map((spec, i) => (
              <div key={i} className="bg-slate-800 p-6 rounded-2xl border border-white/10 hover:border-teal-500/50 transition-all text-center group cursor-pointer" onClick={onStart}>
                <div className="text-4xl mb-4">{spec.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{spec.name}</h3>
                <div className="text-teal-400 font-bold">{spec.courses} קורסים</div>
                <div className="text-slate-500 text-sm">{spec.questions} שאלות</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits List */}
      <div className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">מה תקבלו?</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'גישה לכל הקורסים והחומרים',
              'סימולטורים מלאים למבחני רישוי',
              'סרטוני הסבר לכל נושא',
              'מעקב התקדמות אישי',
              'שאלות עם הסברים מפורטים',
              'תמיכה וליווי צמוד',
              'עדכונים שוטפים בחומר',
              'גישה מכל מכשיר'
            ].map((benefit, i) => (
              <div key={i} className="flex items-center gap-3 bg-slate-800/30 p-4 rounded-xl">
                <CheckCircle className="w-6 h-6 text-teal-400 flex-shrink-0" />
                <span className="text-white font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-16 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
            מוכנים להתחיל?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            הצטרפו לאלפי סטודנטים שכבר לומדים איתנו
          </p>
          
          <button 
            onClick={onStart}
            className="bg-teal-500 text-slate-900 px-16 py-5 rounded-xl text-xl font-black shadow-[0_20px_50px_rgba(20,184,166,0.3)] active:scale-95 transition-all hover:bg-teal-400 animate-pulse"
            data-testid="final-cta-button"
          >
            כניסה מהירה למערכת
          </button>
          
          <div className="mt-6 flex items-center justify-center gap-4">
            <div className="h-px bg-slate-700 w-20"></div>
            <span className="text-slate-500 text-sm">או</span>
            <div className="h-px bg-slate-700 w-20"></div>
          </div>
          
          <div className="mt-6">
            <LoginButton className="bg-white text-slate-900 px-8 py-3 rounded-xl font-bold flex items-center gap-3 mx-auto hover:bg-slate-100 transition-colors shadow-lg">
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              התחבר עם Google
            </LoginButton>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 bg-slate-800 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <button
            onClick={() => setShowContact(true)}
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 font-bold text-sm mb-4 transition-colors"
            data-testid="open-contact-form-btn"
          >
            <Mail className="w-4 h-4" />
            צור קשר
          </button>
          <p className="text-slate-500 text-sm">
            © 2026 הצ'ייסר - כל הזכויות שמורות
          </p>
        </div>
      </div>

      {/* Contact Modal */}
      {showContact && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowContact(false)}
          data-testid="contact-modal-overlay"
        >
          <div
            className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowContact(false)}
              className="absolute top-4 left-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              data-testid="contact-modal-close-btn"
              aria-label="סגור"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>
            <ContactForm onClose={() => setShowContact(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default PromoView;
